import * as React from 'react';
import * as mobxReact from 'mobx-react';
import * as IoC from 'typescript-ioc';
import { KnovioLiveStreamVM, KnovioLiveStreamVariant } from './knovio-live-stream.viewmodel';
import { CommonModal } from '../../../../../global/modal/common/common-modal';
import { FormattedMessage, useIntl } from 'react-intl';
import DefaultButton from '../../../../../global/default-button/default-button';
import { DefaultCloseModalButton } from '../../../../../global/modal/common/common-close-modal-buttons';
import { FormField } from '../../../../../global/form/form-field/form-field';
import './knovio-live-stream-modal.scss';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import { FieldKey, FORM_CONFIG } from './form.config';
import Text from '../../../../../global/text/text';
import { MultiSelectOption } from '../../../../../global/multi-select-list/_multi-select-option';
import { OERadioOptions } from '../../../../../global/oe-radio-options/oe-radio-options';
import { ConnectionType, FlyoutTabOption, KnovioLabel } from '../../../../../../api/connections.dao';
import { RichTextEditor } from '../../../../../global/rich-text-editor';
import { DefaultDatePicker } from '../../../../../global/default-datepicker/default-datepicker';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../../../infrastructure/date-formatter';

type KnovioLiveStreamModalProps = {
    vm: KnovioLiveStreamVM;
};

export const KnovioLiveStreamModal = mobxReact.observer(({ vm }: KnovioLiveStreamModalProps): JSX.Element | null => {
    const { formatMessage } = useIntl();

    const { formVM, startDateTime } = vm;

    const renderModal = () => {
        const dateTimeFormatter = IoC.Container.get(IDateTimeFormatter);
        const dateFormat = dateTimeFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours
            ? DateFnsFormats.StringifiedShortFullDateDay24Hours
            : DateFnsFormats.StringifiedShortFullDateDay12Hours;

        const broadcastDateTime = vm.getFormFieldPropsByKey(FieldKey.BROADCAST_DATE_TIME);
        const topicField = vm.getFormFieldPropsByKey(FieldKey.TOPIC);
        const descriptionField = vm.getFormFieldPropsByKey(FieldKey.DESCRIPTION);
        const neverLateNavigationField = vm.getFormFieldPropsByKey(FieldKey.NEVER_LATE_NAVIGATION);
        const flyoutTabsField = vm.getFormFieldPropsByKey(FieldKey.FLYOUT_TABS);
        const layoutField = vm.getFormFieldPropsByKey(FieldKey.LAYOUT);
        const qaNotificationEmailField = vm.getFormFieldPropsByKey(FieldKey.QA_NOTIFICATION_EMAIL);
        const standByMessageField = vm.getFormFieldPropsByKey(FieldKey.STANDBY_MESSAGE);
        const expirationDateEnabledField = vm.getFormFieldPropsByKey(FieldKey.EXPIRATION_DATE_ENABLED);
        const expirationDateField = vm.getFormFieldPropsByKey(FieldKey.EXPIRATION_DATE);

        const defaultExpirationDate = new Date(startDateTime);
        defaultExpirationDate.setMonth(defaultExpirationDate.getMonth() + 1);

        const expirationDateFieldValue = expirationDateField?.value || defaultExpirationDate;

        const setExpirationDate = (value: Date | null, touched: boolean): void => {
            formVM.setValue(FieldKey.EXPIRATION_DATE, value);
            if (touched) {
                formVM.setValueTouched(FieldKey.EXPIRATION_DATE);
            }
        };
        const modalTypeText = vm.behavior === KnovioLiveStreamVariant.CREATE ? 'Create' : 'Edit';
        const manualBehaviorText = vm.behavior === KnovioLiveStreamVariant.CREATE ? 'Add' : 'Edit';
        const createKnovioTitleId = vm.behavior === KnovioLiveStreamVariant.CREATE ? 'createKnovio' : 'editKnovio';
        const changeDetailsManuallyTitleId = vm.behavior === KnovioLiveStreamVariant.CREATE ? 'createDetailsManually' : 'editDetailsManually';
        const isDisabledActionButton = vm.behavior === KnovioLiveStreamVariant.EDIT ? vm.isUpdatingKnovio : vm.isCreatingKnovio;

        return (
            <CommonModal
                title={<FormattedMessage id={`oe.knovioLiveStreamModal.title.${vm.behavior}`} defaultMessage={`${modalTypeText} ${KnovioLabel.OEStream}`} />}
                isOpen={vm.isOpened}
                onRequestClose={vm.closeModal}
                className='oe-modal-width create-knovio-livestream-modal'
                classNameBody='body__scrollable'
                footer={
                    <div className='buttons-block'>
                        <DefaultButton
                            text={<FormattedMessage
                                    id={`oe.knovioLiveStreamModal.${changeDetailsManuallyTitleId}`}
                                    defaultMessage={`${manualBehaviorText} Details Manually`}/>}
                            modifier={['fat', 'cancel']}
                            onClick={vm.openKnovioConnectionForm.bind(vm, { type: ConnectionType.Knovio })}
                            disabled={vm.isUpdatingKnovio}/>
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultButton
                                text={<FormattedMessage
                                        id={`oe.knovioLiveStreamModal.${createKnovioTitleId}`}
                                        defaultMessage={`${modalTypeText} ${KnovioLabel.OEStream}`}
                                    />}
                                onClick={vm.onSubmitKnovio}
                                modifier={['fat', 'confirm-safe']}
                                disabled={isDisabledActionButton}
                            />
                            <DefaultCloseModalButton/>
                        </div>
                    </div>
                }
            >
                <form className='oe-form'>
                    <FormField
                        className='m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.BROADCAST_DATE_TIME].labelDescriptor} />}
                    >
                        <input
                            className='oe-form-field__control'
                            value={broadcastDateTime.value as string}
                            type='text'
                            disabled={true}
                        />
                    </FormField>
                    <FormField
                        className='m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.TOPIC].labelDescriptor} />}
                    >
                        <input
                            className='oe-form-field__control'
                            value={topicField.value as string}
                            type='text'
                            onChange={e => formVM.setValue(FieldKey.TOPIC, e.target.value)}
                            onBlur={formVM.setValueTouched.bind(formVM, FieldKey.TOPIC)}
                        />
                        <span className='oe-form-field__error'>
                            {
                                !topicField.valid && topicField.touched &&
                                <FormattedMessage
                                    values={{ fieldName: formatMessage(FORM_CONFIG[FieldKey.TOPIC].labelDescriptor) }}
                                    {...topicField.validationMessage}
                                />
                            }
                        </span>
                    </FormField>
                    <FormField
                        className='m-bottom-30px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.DESCRIPTION].labelDescriptor} />}
                    >
                        <textarea
                            rows={3}
                            style={{ resize: 'vertical' }}
                            className='oe-form-field__control'
                            value={descriptionField.value as string}
                            onChange={e => formVM.setValue(FieldKey.DESCRIPTION, e.target.value)}
                            onBlur={formVM.setValueTouched.bind(formVM, FieldKey.DESCRIPTION)}
                        />
                        <span className='oe-form-field__error'>
                            {
                                !descriptionField.valid && descriptionField.touched &&
                                <FormattedMessage
                                    values={{ fieldName: formatMessage(FORM_CONFIG[FieldKey.DESCRIPTION].labelDescriptor) }}
                                    {...descriptionField.validationMessage}
                                />
                            }
                        </span>
                    </FormField>
                    <FormField className='form-toggle m-bottom-30px'>
                        <OEToggle
                            isSelected={neverLateNavigationField.value as boolean}
                            onClick={() => formVM.setValue(FieldKey.NEVER_LATE_NAVIGATION, !neverLateNavigationField.value)}
                        />
                        {neverLateNavigationField.value
                            ? <Text message='NeverLate® navigation is enabled' id='oe.knovioLiveStream.neverLate.enabled' />
                            : <Text message='NeverLate® navigation is disabled' id='oe.knovioLiveStream.neverLate.disabled' />
                        }
                    </FormField>
                    <FormField
                        className='m-bottom-30px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.FLYOUT_TABS].labelDescriptor} />}
                    >
                        <div className='oe-form__row'>
                            {
                                vm.flyoutTabsOptions.map(({ label, value }) => {
                                    const isSelected = (flyoutTabsField.value as FlyoutTabOption[]).includes(value);
                                    const updateFlyoutTabsOptions = vm.updateFlyoutTabsOptions.bind(vm, value);

                                    return <MultiSelectOption
                                        key={label}
                                        isSelected={isSelected}
                                        name={label}
                                        isCheckboxInLeft={true}
                                        onCheckboxClick={updateFlyoutTabsOptions}
                                        onClick={updateFlyoutTabsOptions}
                                    />;
                                })
                            }
                        </div>
                    </FormField>
                    <FormField
                        className='layout-options'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.LAYOUT].labelDescriptor} />}
                    >
                        <OERadioOptions
                            value={layoutField.value as string}
                            onOptionToggle={option => formVM.setValue(FieldKey.LAYOUT, option)}
                            options={vm.layoutOptions}
                            isIconFirst
                        />
                    </FormField>

                    <FormField
                        className='m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.QA_NOTIFICATION_EMAIL].labelDescriptor} />}
                    >
                        <input
                            className='oe-form-field__control'
                            value={qaNotificationEmailField.value as string}
                            type='text'
                            onChange={e => formVM.setValue(FieldKey.QA_NOTIFICATION_EMAIL, e.target.value)}
                            onBlur={formVM.setValueTouched.bind(formVM, FieldKey.QA_NOTIFICATION_EMAIL)}
                        />
                        <span className='oe-form-field__error'>
                            {
                                !qaNotificationEmailField.valid && qaNotificationEmailField.touched &&
                                <FormattedMessage
                                    values={{ fieldName: formatMessage(FORM_CONFIG[FieldKey.QA_NOTIFICATION_EMAIL].labelDescriptor) }}
                                    {...qaNotificationEmailField.validationMessage}
                                />
                            }
                        </span>

                        <div className='oe-form-field__title'>Please enter a comma-separated list of email addresses.</div>
                    </FormField>
                    <FormField
                        className='m-bottom-20px'
                        label={<FormattedMessage {...FORM_CONFIG[FieldKey.STANDBY_MESSAGE].labelDescriptor} />}
                    >
                        <RichTextEditor
                            className='standBy-message oe-form-field__control'
                            value={standByMessageField.value as string}
                            onChange={(_, editor) => formVM.setValue(FieldKey.STANDBY_MESSAGE, editor.getData())}
                            onBlur={formVM.setValueTouched.bind(formVM, FieldKey.STANDBY_MESSAGE)}
                            validationError={
                                <span className='oe-form-field__error'>
                                    {
                                        !standByMessageField.valid && standByMessageField.touched &&
                                        <FormattedMessage
                                            values={{ fieldName: formatMessage(FORM_CONFIG[FieldKey.STANDBY_MESSAGE].labelDescriptor) }}
                                            {...standByMessageField.validationMessage}
                                        />
                                    }
                                </span>
                            }
                        />
                    </FormField>
                    <FormField className='form-toggle m-bottom-30px'>
                            <OEToggle
                                isSelected={expirationDateEnabledField.value as boolean}
                                onClick={() => {
                                    formVM.setValue(FieldKey.EXPIRATION_DATE_ENABLED, !expirationDateEnabledField.value);
                                    setExpirationDate(formVM.getValue(FieldKey.EXPIRATION_DATE_ENABLED) ? defaultExpirationDate : null, false);
                                }}
                            />
                            {expirationDateEnabledField.value
                                ? <Text message='Expiration date is set' id='oe.knovioLiveStream.expirationDate.enabled' />
                                : <Text message='No expiration date is set' id='oe.knovioLiveStream.expirationDate.disabled' />
                            }
                        </FormField>
                    {expirationDateEnabledField.value
                        && <FormField className='m-bottom-20px'>
                            <DefaultDatePicker
                                className='oe-form-field__control'
                                id='meetingDate'
                                dateformat={dateFormat}
                                showTimeSelect={false}
                                selected={expirationDateFieldValue as Date}
                                onChange={date => setExpirationDate(date, true)}
                                openToDate={expirationDateFieldValue as Date || defaultExpirationDate}
                                minDate={new Date()}

                            />
                            <span className='oe-form-field__error'>
                                {
                                    !expirationDateField.valid && expirationDateField.touched &&
                                    <FormattedMessage
                                        values={{ fieldName: formatMessage(FORM_CONFIG[FieldKey.EXPIRATION_DATE].labelDescriptor) }}
                                        {...expirationDateField.validationMessage}
                                    />
                                }
                            </span>
                        </FormField>
                    }
                </form>
            </CommonModal>
        );
    };

    return formVM ? renderModal() : null;
});
